import type {
  CouponPrizePayload,
  LuckyNumberPrizePayload,
  NonePrizePayload,
  PixPrizePayload,
  RoulettePrizeType,
} from '@/lib/roulette'
import styles from './CampanhasPage.module.css'

export interface RoletaPrizeTypeFieldsProps {
  prizeType: RoulettePrizeType
  couponPayload: CouponPrizePayload | null
  luckyPayload: LuckyNumberPrizePayload | null
  pixPayload: PixPrizePayload | null
  nonePayload: NonePrizePayload | null
  onCouponChange: (patch: Partial<CouponPrizePayload>) => void
  onLuckyChange: (patch: Partial<LuckyNumberPrizePayload>) => void
  onPixChange: (patch: Partial<PixPrizePayload>) => void
  onNoneChange: (patch: Partial<NonePrizePayload>) => void
}

export function RoletaPrizeTypeFields({
  prizeType,
  couponPayload,
  luckyPayload,
  pixPayload,
  nonePayload,
  onCouponChange,
  onLuckyChange,
  onPixChange,
  onNoneChange,
}: RoletaPrizeTypeFieldsProps) {
  if (prizeType === 'coupon' && couponPayload) {
    return (
      <div className={styles.formSection} style={{ marginTop: 'var(--spacing-md)' }}>
        <h5 className={styles.sectionSubtitle} style={{ marginBottom: 'var(--spacing-sm)', fontWeight: 600 }}>
          Cupom de desconto
        </h5>
        <p style={{ margin: '0 0 var(--spacing-md)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          Preencha apenas os campos que fizerem sentido para o cupom sorteado. O código será gerado automaticamente na hora do sorteio.
        </p>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label htmlFor="roleta-coupon-desc">Descrição exibida ao comprador</label>
            <input
              id="roleta-coupon-desc"
              type="text"
              value={couponPayload.description ?? ''}
              onChange={(e) => onCouponChange({ description: e.target.value })}
              className={styles.input}
              placeholder="Exemplo: 10% em qualquer compra"
              autoComplete="off"
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="roleta-coupon-percent">Desconto em % (opcional)</label>
            <input
              id="roleta-coupon-percent"
              type="number"
              min={0}
              max={100}
              value={couponPayload.discount_percent ?? ''}
              onChange={(e) =>
                onCouponChange({
                  discount_percent: e.target.value === '' ? undefined : Number.parseFloat(e.target.value),
                })
              }
              className={styles.input}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="roleta-coupon-amount">Desconto em R$ (opcional)</label>
            <input
              id="roleta-coupon-amount"
              type="number"
              min={0}
              step={0.01}
              value={couponPayload.discount_amount ?? ''}
              onChange={(e) =>
                onCouponChange({
                  discount_amount: e.target.value === '' ? undefined : Number.parseFloat(e.target.value),
                })
              }
              className={styles.input}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="roleta-coupon-min">Compra mínima em R$ (opcional)</label>
            <input
              id="roleta-coupon-min"
              type="number"
              min={0}
              step={0.01}
              value={couponPayload.min_purchase ?? ''}
              onChange={(e) =>
                onCouponChange({
                  min_purchase: e.target.value === '' ? undefined : Number.parseFloat(e.target.value),
                })
              }
              className={styles.input}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor="roleta-coupon-valid">Validade (AAAA-MM-DD, opcional)</label>
            <input
              id="roleta-coupon-valid"
              type="text"
              inputMode="numeric"
              value={couponPayload.valid_until ?? ''}
              onChange={(e) => onCouponChange({ valid_until: e.target.value.trim() || undefined })}
              className={styles.input}
              placeholder="2026-12-31"
              autoComplete="off"
            />
          </div>
        </div>
      </div>
    )
  }

  if (prizeType === 'lucky_number' && luckyPayload) {
    return (
      <div className={styles.formSection} style={{ marginTop: 'var(--spacing-md)' }}>
        <h5 className={styles.sectionSubtitle} style={{ marginBottom: 'var(--spacing-sm)', fontWeight: 600 }}>
          Número da sorte
        </h5>
        <p style={{ margin: '0 0 var(--spacing-md)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          Quantidade extra de números da sorte concedidos quando esta fatia for sorteada.
        </p>
        <div className={styles.field} style={{ maxWidth: 200 }}>
          <label htmlFor="roleta-lucky-qty">Quantidade</label>
          <input
            id="roleta-lucky-qty"
            type="number"
            min={1}
            max={1000}
            value={luckyPayload.quantity ?? 1}
            onChange={(e) =>
              onLuckyChange({ quantity: Number.parseInt(e.target.value, 10) || 1 })
            }
            className={styles.input}
          />
        </div>
      </div>
    )
  }

  if (prizeType === 'pix' && pixPayload) {
    return (
      <div className={styles.formSection} style={{ marginTop: 'var(--spacing-md)' }}>
        <h5 className={styles.sectionSubtitle} style={{ marginBottom: 'var(--spacing-sm)', fontWeight: 600 }}>
          Prêmio em PIX
        </h5>
        <p style={{ margin: '0 0 var(--spacing-md)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          Valor em reais pago ao comprador quando esta fatia for sorteada (conforme regras da campanha).
        </p>
        <div className={styles.field} style={{ maxWidth: 200 }}>
          <label htmlFor="roleta-pix-amount">Valor (R$)</label>
          <input
            id="roleta-pix-amount"
            type="number"
            min={0}
            step={0.01}
            value={pixPayload.amount ?? 0}
            onChange={(e) =>
              onPixChange({ amount: Number.parseFloat(e.target.value) || 0 })
            }
            className={styles.input}
          />
        </div>
      </div>
    )
  }

  if (prizeType === 'none' && nonePayload) {
    return (
      <div className={styles.formSection} style={{ marginTop: 'var(--spacing-md)' }}>
        <h5 className={styles.sectionSubtitle} style={{ marginBottom: 'var(--spacing-sm)', fontWeight: 600 }}>
          Sem prêmio
        </h5>
        <p style={{ margin: '0 0 var(--spacing-md)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          Texto amigável exibido na fatia quando o comprador não ganha um prêmio material.
        </p>
        <div className={styles.field}>
          <label htmlFor="roleta-none-label">Mensagem na fatia</label>
          <input
            id="roleta-none-label"
            type="text"
            value={nonePayload.label ?? '😞'}
            onChange={(e) => onNoneChange({ label: e.target.value })}
            className={styles.input}
            placeholder="Ex.: 😞 ou uma mensagem curta"
            autoComplete="off"
          />
        </div>
      </div>
    )
  }

  return null
}
